import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png"; // Update with your

function LibrarySetup() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [librariesList, setLibrariesList] = useState([]);
  const [filteredLibrariesList, setFilteredLibrariesList] = useState([]); // Add filtered list

  const [searchTerm, setSearchTerm] = useState("");

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      id: 0,
      name: "",
      email: "",
      location: "",
      openTime: "",
      closeTime: "",
      description: "",
      type: "",
      maxBookPerMember: "",
      centerId: "",
      createdBy: localStorage.getItem("userId"),
    },
  });

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [centersData, setCentersData] = useState([]);
  //const [librariesList, setLibrariesList] = useState([]);

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setCentersData(response);

        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
          }
        }

        // if (response.length > 0) {
        //   const firstCenterId = response[0].id; // Get the first center ID
        //   setCenterId(firstCenterId); // Set as selected center ID
        //   getLibrariesListByCenterId(firstCenterId); // Fetch students with the first center ID
        // }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCenters();
  }, []);

  const getLibrariesListByCenterId = async (centerId) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `LMS/GetLibraryByCenterIdAsync?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLibrariesList(response?.libraryService);
      setFilteredLibrariesList(response?.libraryService || []);
    } catch (error) {
      console.error("Error fetching books:", error);
    }
  };

  useEffect(() => {
    getLibrariesListByCenterId(centerId);
  }, [triggerRerender]);

  const handleCenterDropdownChange = (event) => {
    const selectedCenterId = event.target.value;
    setCenterId(selectedCenterId);
    getLibrariesListByCenterId(selectedCenterId);
  };

  const handleEdit = async (libraryId) => {
    try {
      const response = await get(
        `LMS/GetLibraryByIDAsync?libraryId=${libraryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { libraryService: singleLibraryData } = response;

      reset({
        ...singleLibraryData,
        id: singleLibraryData.id,
        updatedBy: localStorage.getItem("userId"),
      });
    } catch (error) {
      console.error("Error editing author:", error);
    }
  };
  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "type",
        header: "Type",
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEdit(row.original.id)}
            >
              <i className="fas fa-pencil"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const dataToExport = filteredLibrariesList.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
      Type: row.type,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Libraries");
    XLSX.writeFile(workbook, "Libraries.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    
    const roleIdLoggedIn = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleIdLoggedIn == "2" ? orgLogo : roleIdLoggedIn == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Libraries List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "actions" && column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = filteredLibrariesList.map((row, index) => {
        return [
          index + 1,
          row.name,
          row.type,
        ];
      });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("LibrariesList.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formData = {
      ...data,
      centerId: centerId,
    };

    try {
      const response = await post("LMS/AddUpdateLibraryAsync", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        reset({
          id: 0,
          name: "",
          email: "",
          location: "",
          openTime: "",
          closeTime: "",
          description: "",
          type: "",
          maxBookPerMember: "",
          centerId: "",
          createdBy: localStorage.getItem("userId"),
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };
  useEffect(() => {
    const filtered = librariesList.filter((library) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        library.name.toLowerCase().includes(searchLower) ||
        library.type.toLowerCase().includes(searchLower)
      );
    });
    setFilteredLibrariesList(filtered);
  }, [searchTerm, librariesList]);

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Libraries</h2>
        <div className="ms-auto">
          <div className="d-flex align-items-center" style={{ width: "220px" }}>
            <label className="form-label mb-0 me-2">Center</label>
            <select
              className="form-select"
              value={centerId}
              onChange={handleCenterDropdownChange}
              disabled={savedCenterId !== "0"}
            >
              {centersData.map((center) => (
                <option key={center.id} value={center.id}>
                  {center.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <form
                className="row g-3 new-form-style"
                onSubmit={handleSubmit(saveData)}
              >
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Name<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`name`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("name")}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name.message}</small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Library Type<span className="text-danger fs-9">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register(`type`, {
                      required: "This field is required.",
                    })}
                  >
                    <option value="">Select</option>
                    <option value="General">General</option>
                    <option value="Departmental">Departmental</option>
                    <option value="Juniors">Juniors</option>
                    <option value="Seniors">Seniors</option>
                    <option value="Others">Others</option>
                  </select>
                  {errors.type && (
                    <small className="text-danger">{errors.type.message}</small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Opening Time<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`openTime`, {
                      required: "This field is required.",
                    })}
                    type="time"
                    className="form-control"
                    placeholder=""
                  />
                  {errors.openTime && (
                    <small className="text-danger">
                      {errors.openTime.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Closing Time<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`closeTime`, {
                      required: "This field is required.",
                    })}
                    type="time"
                    className="form-control"
                    placeholder=""
                  />
                  {errors.closeTime && (
                    <small className="text-danger">
                      {errors.closeTime.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Max Books Per Member
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    {...register(`maxBookPerMember`, {
                      valueAsNumber: true,
                      required: "This field is required",
                      validate: (value) =>
                        value >= 0 || "Please enter correct value.",
                    })}
                    onKeyDown={(e) => {
                      if (
                        e.key === "-" ||
                        e.key === "e" ||
                        e.key === "+" ||
                        e.key === "."
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value < 0 ? 1 : e.target.value;
                    }}
                  />
                  {errors.maxBookPerMember && (
                    <small className="text-danger">
                      {errors.maxBookPerMember.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">Email</label>
                  <input
                    {...register(`email`, {
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Please enter a valid email.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("email")}
                  />
                  {errors.email && (
                    <small className="text-danger">
                      {errors.email.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">Location</label>
                  <input
                    {...register(`location`, {
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("location")}
                  />
                  {errors.location && (
                    <small className="text-danger">
                      {errors.location.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Description
                  </label>
                  <input
                    {...register(`description`, {
                      maxLength: {
                        value: 250,
                        message: "Maximum of 250 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("description")}
                  />
                  {errors.description && (
                    <small className="text-danger">
                      {errors.description.message}
                    </small>
                  )}
                </div>
                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={filteredLibrariesList}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                    columnPinning: { right: ["actions"] },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default LibrarySetup;
